import { useMemo, useState } from 'react';
import { Button, Input, InputGroup, Loader, Message } from 'rsuite';
import { Button as NsiButton, Modal } from '@nsi-advisory-services/components';
import { useEmployeeConnectCommitments } from '../hooks/useQueries';
import '../pages/ConfigurationSheet.css';
import './EmployeeConnectCommitmentsModal.css';

// Read-only view of every employee's current hours commitment, as EmployeeConnect (the system of
// record) reports it. EC works the figures out; this only displays them. Fetched when the modal
// opens, not with the page.

const DASH = '—';

const formatDate = (iso, { withYear = true } = {}) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.toLocaleDateString(undefined, withYear
    ? { month: 'short', day: 'numeric', year: 'numeric' }
    : { month: 'short', day: 'numeric' });
};

const formatHours = (value) => {
  if (value === null || value === undefined || value === '') return DASH;
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 2 }) : DASH;
};

const hasValue = (v) => v !== null && v !== undefined && v !== '';

// EC doesn't send the classification, so the commitment is read from whichever figure is set: a
// TAWH percent (exempt) or a target per pay period (non-exempt). Neither means no target is set,
// which is not the same as a target of zero.
const commitmentText = (c) => {
  if (hasValue(c?.tawh_percent)) return `${c.tawh_percent}% of TAWH`;
  if (hasValue(c?.target_hours_per_pay_period)) return `${formatHours(c.target_hours_per_pay_period)} hrs / pay period`;
  return 'No target set';
};

const sortName = (e) => String(e?.display_name || [e?.first_name, e?.last_name].filter(Boolean).join(' ') || e?.email || '');

export default function EmployeeConnectCommitmentsModal({ open, onClose }) {
  const [search, setSearch] = useState('');
  const query = useEmployeeConnectCommitments({ enabled: open });
  const payload = query.data;

  const employees = useMemo(
    () => (Array.isArray(payload?.data) ? payload.data : [])
      .slice()
      .sort((a, b) => sortName(a).localeCompare(sortName(b), undefined, { sensitivity: 'base' })),
    [payload?.data]
  );

  const needle = search.trim().toLowerCase();
  const visible = needle
    ? employees.filter((e) => sortName(e).toLowerCase().includes(needle) || String(e?.email || '').toLowerCase().includes(needle))
    : employees;

  const error = query.error;
  const isPermissionError = error && (error.status === 401 || error.status === 403);

  return (
    <Modal open={open} onClose={onClose} size="lg" overflow className="ppc-ec-commitments-modal">
      <Modal.Header>
        <Modal.Title>Hours commitment from EmployeeConnect</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="ppc-cfg-tablescope ppc-ec-commitments">
          <div className="ppc-ec-commitments__toolbar">
            <InputGroup inside className="ppc-ec-commitments__search">
              <InputGroup.Addon>
                <i className="bi bi-search" aria-hidden="true" />
              </InputGroup.Addon>
              <Input value={search} onChange={setSearch} placeholder="Search by name or email" aria-label="Search employees" />
            </InputGroup>
            {payload ? (
              <span className="ppc-ec-commitments__meta">
                {payload.as_of ? `As of ${formatDate(payload.as_of) || payload.as_of}` : null}
                {payload.as_of ? ' · ' : null}
                {`${Number(payload.count ?? employees.length)} employee${Number(payload.count ?? employees.length) === 1 ? '' : 's'}`}
              </span>
            ) : null}
            <Button
              size="sm"
              appearance="ghost"
              onClick={() => query.refetch()}
              loading={query.isFetching}
              disabled={!open}
              startIcon={<i className="bi bi-arrow-clockwise" aria-hidden="true" />}
            >
              Refresh
            </Button>
          </div>

          {query.isLoading ? (
            <div className="ppc-ec-commitments__state">
              <Loader content="Loading commitments from EmployeeConnect…" />
            </div>
          ) : error ? (
            <Message type={isPermissionError ? 'warning' : 'error'} showIcon>
              <strong>
                {isPermissionError
                  ? 'EmployeeConnect refused the request.'
                  : 'Could not load hours commitments from EmployeeConnect.'}
              </strong>{' '}
              {error.message}
            </Message>
          ) : employees.length === 0 ? (
            <div className="ppc-ec-commitments__state">EmployeeConnect returned no employees.</div>
          ) : visible.length === 0 ? (
            <div className="ppc-ec-commitments__state">No employees match &ldquo;{search.trim()}&rdquo;.</div>
          ) : (
            <div className="nsi-table-wrap">
              <table className="nsi-table">
                <thead>
                  <tr>
                    <th scope="col">Employee</th>
                    <th scope="col">Commitment</th>
                    <th scope="col">Since</th>
                    <th scope="col">In ProPlan</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((e) => {
                    const c = e?.current || {};
                    const since = formatDate(c.effective_date);
                    const match = e?.proplan_user;
                    return (
                      <tr key={e?.employee_id || e?.azure_oid || e?.email}>
                        <td>
                          <div className="ppc-ec-commitments__name">{sortName(e) || DASH}</div>
                          {e?.email ? <div className="ppc-ec-commitments__email">{e.email}</div> : null}
                        </td>
                        <td
                          className={
                            hasValue(c.tawh_percent) || hasValue(c.target_hours_per_pay_period)
                              ? undefined
                              : 'ppc-ec-commitments__muted'
                          }
                        >
                          {commitmentText(c)}
                        </td>
                        <td className={since ? undefined : 'ppc-ec-commitments__muted'}>{since || 'From employee record'}</td>
                        <td>
                          {match ? (
                            <span
                              className="ppc-ec-commitments__matched"
                              title={`Matched to ${match.name || 'a ProPlan user'} by ${match.matched_by === 'oid' ? 'Entra ID' : 'email'}`}
                            >
                              <i className="bi bi-check-circle-fill" aria-hidden="true" />
                              <span className="ppc-ec-commitments__sr">In ProPlan</span>
                            </span>
                          ) : (
                            <span className="ppc-ec-commitments__muted">Not in ProPlan</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <NsiButton onClick={onClose}>Close</NsiButton>
      </Modal.Footer>
    </Modal>
  );
}
